import { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { clearError } from '@store/features/content/contentSlice';
import { resetStatus } from '@store/features/app/appSlice';
import {
  Button,
  Textarea,
  Paper,
  Text,
  Table,
  ScrollArea,
  Box,
  Title,
  Group,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { Content } from '@services/content-service';
import { useCreateContent } from '@hooks/useCreateContent';
import styles from './NewContent.module.scss';
import { getFlagEmoji } from '@/utils/languageUtils';
import { extractHostname } from '@/utils/urlUtils';

interface NewContentProps {
  isDark: boolean;
}

const NewContent = ({ isDark }: NewContentProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { contents } = useAppSelector(state => state.content);
  const { createContent, isCreating, error: createError, errorDetails } = useCreateContent();
  const [jsonInput, setJsonInput] = useState('');
  const [validationError, setValidationError] = useState('');
  const [clearButtonColor, setClearButtonColor] = useState(isDark ? 'gray' : 'blue');
  const [submitButtonColor, setSubmitButtonColor] = useState('blue');
  const [previousContentsLength, setPreviousContentsLength] = useState(contents.length);
  const [parsedData, setParsedData] = useState<Content[]>([]);
  const [showTable, setShowTable] = useState(false);

  // Update component styles when theme changes
  useEffect(() => {
    setClearButtonColor(isDark ? 'gray' : 'blue');
    setSubmitButtonColor(isDark ? 'gray' : 'blue');
  }, [isDark]);

  // Mostrar notificación de error cuando cambia el estado de error
  useEffect(() => {
    if (createError) {
      notifications.show({
        title: t('newContent.errors.submitError'),
        message: errorDetails || createError,
        color: 'red',
        icon: <IconX size={16} />,
        autoClose: 5000,
        withBorder: true,
      });
    }
  }, [createError, errorDetails, t]);

  // Mostrar notificación de éxito cuando se añaden nuevos contenidos
  useEffect(() => {
    if (!isCreating && contents.length > previousContentsLength) {
      notifications.show({
        title: t('common.success'),
        message: t('newContent.success'),
        color: 'green',
        icon: <IconCheck size={16} />,
        autoClose: 3000,
        withBorder: true,
      });
      setPreviousContentsLength(contents.length);
    }
  }, [isCreating, contents.length, previousContentsLength, t]);

  const validateJSON = (input: string): boolean => {
    try {
      const parsed = JSON.parse(input);
      if (!Array.isArray(parsed)) {
        setValidationError(t('newContent.errors.notArray'));
        setShowTable(false);
        return false;
      }
      if (parsed.length === 0) {
        setValidationError(t('newContent.errors.emptyArray'));
        setShowTable(false);
        return false;
      }

      // Validar que cada elemento tenga las propiedades requeridas
      const isValidContent = parsed.every(
        item =>
          typeof item === 'object' &&
          item !== null &&
          typeof item.date === 'string' &&
          typeof item.content === 'string' &&
          typeof item.source === 'string'
      );

      if (!isValidContent) {
        setValidationError(t('newContent.errors.invalidContent'));
        setShowTable(false);
        return false;
      }

      // Procesar los datos para asegurar que tengan todos los campos necesarios
      const processedData = parsed.map(item => ({
        ...item,
        // Asignar valores por defecto para campos opcionales
        status: item.status || 'P', // Por defecto 'P' (Publicado)
        lang: item.lang || 'es', // Por defecto 'es' (Español)
      }));

      // Guardar los datos procesados para mostrar en la tabla
      setParsedData(processedData as Content[]);
      setShowTable(true);
      setValidationError('');
      return true;
    } catch (err) {
      setValidationError(t('newContent.errors.invalidJSON'));
      setShowTable(false);
      return false;
    }
  };

  const handleJsonChange = (event: { target: { value: string } }) => {
    const input = event.target.value;
    setJsonInput(input);

    // Intentar validar el JSON si hay contenido
    if (input.trim()) {
      validateJSON(input);
    } else {
      setShowTable(false);
      setValidationError('');
    }
  };

  const handleSubmit = async () => {
    if (!validateJSON(jsonInput)) return;

    try {
      // Usar el hook para crear el contenido
      await createContent(parsedData);
      setJsonInput('');
      setShowTable(false);
      // La notificación de éxito se mostrará automáticamente cuando se actualice el estado
    } catch (err) {
      // Error is handled by the hook and displayed through the error state
    }
  };

  const handleClear = () => {
    // Limpiar el input y los errores de validación
    setJsonInput('');
    setValidationError('');
    setShowTable(false);
    setParsedData([]);

    // Reiniciar el estado de la aplicación
    dispatch(clearError());
    dispatch(resetStatus());
  };

  const renderCellValue = (key: string, value: unknown): ReactNode => {
    switch (key) {
      case 'lang':
        return (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span>{getFlagEmoji(value as string)}</span>
          </Box>
        );
      case 'source': {
        try {
          const host = extractHostname(value as string);
          return (
            <a href={value as string} target="_blank" rel="noopener noreferrer">
              {host}
            </a>
          );
        } catch {
          return value as string;
        }
      }
      case 'content':
        return <div className={styles['newContent__tablePreview--content']}>{String(value)}</div>;
      default:
        return String(value);
    }
  };

  return (
    <div className={styles.newContent__container}>
      <Paper
        shadow="sm"
        p="md"
        radius="md"
        withBorder
        className={styles.newContent__paper}
        bg={isDark ? 'dark.6' : 'gray.0'}
      >
        <Text className={styles.newContent__title}>{t('newContent.title')}</Text>
        <Text className={styles.newContent__description}>{t('newContent.description')}</Text>

        <form className={styles.newContent__form} onSubmit={e => e.preventDefault()}>
          <Textarea
            className={styles.newContent__textarea}
            data-dark={isDark}
            value={jsonInput}
            onChange={handleJsonChange}
            placeholder={t('newContent.placeholder')}
            error={validationError}
            disabled={isCreating}
            autosize
            minRows={10}
            maxRows={25}
            radius="md"
            size="md"
            variant={isDark ? 'filled' : 'default'}
          />

          {showTable && parsedData.length > 0 && (
            <>
              <Divider my="md" />
              <Box mt="md" mb="md">
                <Title order={4} mb="xs">
                  {t('newContent.preview')}
                </Title>
                <ScrollArea h={300} className={styles.newContent__tablePreview}>
                  <Table highlightOnHover verticalSpacing="sm">
                    <thead>
                      <tr>
                        <th>{t('newContent.table.date')}</th>
                        <th>{t('newContent.table.content')}</th>
                        <th>{t('newContent.table.source')}</th>
                        <th>{t('newContent.table.lang')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((item, index) => (
                        <tr key={index}>
                          <td>{renderCellValue('date', item.date)}</td>
                          <td className={styles['newContent__tablePreview--content']}>
                            {renderCellValue('content', item.content)}
                          </td>
                          <td className={styles['newContent__tablePreview--source']}>
                            {renderCellValue('source', item.source)}
                          </td>
                          <td>{renderCellValue('lang', item.lang)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollArea>
              </Box>
            </>
          )}

          <Group justify="flex-end" gap="sm" mt="lg" className={styles.newContent__actions}>
            <Button
              leftSection={<IconX size={14} />}
              variant="subtle"
              c={isDark ? 'lightgray' : 'white'}
              onClick={handleClear}
              disabled={isCreating || !jsonInput}
              bg={clearButtonColor}
            >
              {t('common.clear')}
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              onClick={handleSubmit}
              c={isDark ? 'lightgray' : 'white'}
              loading={isCreating}
              disabled={!jsonInput}
              bg={submitButtonColor}
            >
              {t('common.submit')}
            </Button>
          </Group>
        </form>
      </Paper>
    </div>
  );
};

export default NewContent;
